import {
  faChevronDown,
  faChevronUp,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import CookieTable from '~/components/CookieTable';
import { Button } from '~/components/ui/Button';
import { getCookie, setCookie, updateConsent } from '~/helpers/cookies';
import { cn } from '~/helpers/utils';
import type { Cookie, CookiePreferences } from '~/types/Cookie';

// TODO: Move to somewhere manageable like Moderating Tool
const cookieData: Record<'necessary' | 'statistics' | 'marketing', Cookie[]> = {
  necessary: [
    {
      name: '.AspNetCore.Session',
      provider: 'Advieskeuze',
      purpose: 'Behoudt de sessiestatus van de gebruiker bij pagina-aanvragen.',
      duration: 'Sessie',
    },
    {
      name: '.AspNetCore.AntiForgery',
      provider: 'Advieskeuze',
      purpose:
        'Beschermt tegen Cross-Site Request Forgery (CSRF) bij formulieren en API-aanvragen.',
      duration: 'Sessie',
    },
    {
      name: 'ARRAffinity',
      provider: 'Advieskeuze',
      purpose:
        'Zorgt ervoor dat verzoeken van dezelfde gebruiker naar dezelfde server worden gestuurd voor load balancing.',
      duration: 'Sessie',
    },
    {
      name: 'ARRAffinitySameSite',
      provider: 'Advieskeuze',
      purpose:
        'Werkt samen met ARRAffinity en voegt SameSite attribuut toe voor extra beveiliging.',
      duration: 'Sessie',
    },
    {
      name: 'advieskeuze-routing',
      provider: 'Advieskeuze',
      purpose:
        'Deze cookie zorgt ervoor dat je tijdens je bezoek steeds bij dezelfde omgeving van onze website uitkomt.',
      duration: '24 uur',
    },
    {
      name: 'advieskeuze_cookie_consent',
      provider: 'Advieskeuze',
      purpose: 'Slaat de cookie voorkeuren van de gebruiker op.',
      duration: '1 jaar',
    },
  ],
  statistics: [
    {
      name: '_ga',
      provider: 'Google Analytics',
      purpose:
        'Maakt onderscheid tussen unieke gebruikers en berekent gegevens over bezoekers, sessies en campagnes.',
      duration: '2 jaar',
      link: 'https://policies.google.com/privacy',
    },
    {
      name: '_ga_{containerId}',
      provider: 'Google Analytics',
      purpose:
        'Wordt door Google Analytics gebruikt om de sessiestatus te bewaren.',
      duration: '2 jaar',
      link: 'https://policies.google.com/privacy',
    },
    {
      name: '_clck',
      provider: 'Microsoft Clarity',
      purpose:
        'Verzamelt een unieke, pseudonieme gebruikers-ID en voorkeuren voor Clarity-analyses.',
      duration: '1 jaar',
      link: 'https://privacy.microsoft.com/nl-nl/privacystatement',
    },
    {
      name: '_clsk',
      provider: 'Microsoft Clarity',
      purpose:
        'Verbindt meerdere pagina‑weergaven van een bezoeker tot één Clarity‑sessie.',
      duration: '1 dag',
      link: 'https://privacy.microsoft.com/nl-nl/privacystatement',
    },
  ],
  marketing: [],
};

const CookieConsent: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [statisticsConsent, setStatisticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const consent = getCookie('advieskeuze_cookie_consent');

    if (!consent) {
      setOpen(true);
      return;
    }

    try {
      const preferences: CookiePreferences = JSON.parse(consent);
      setStatisticsConsent(!!preferences.statistics);
      setMarketingConsent(!!preferences.marketing);
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    const openCookieSettings = () => {
      const consent = getCookie('advieskeuze_cookie_consent');

      if (consent) {
        try {
          const preferences: CookiePreferences = JSON.parse(consent);
          setStatisticsConsent(!!preferences.statistics);
          setMarketingConsent(!!preferences.marketing);
        } catch {}
      }

      setOpen(true);
    };

    const resetConsent = () => {
      setCookie('advieskeuze_cookie_consent', '', -1);
      setStatisticsConsent(false);
      setMarketingConsent(false);
      setOpen(true);
    };

    window.addEventListener('open-cookie-settings', openCookieSettings);
    window.addEventListener('reset-cookie-settings', resetConsent);

    return () => {
      window.removeEventListener('open-cookie-settings', openCookieSettings);
      window.removeEventListener('reset-cookie-settings', resetConsent);
    };
  }, []);

  const savePreferences = (statistics: boolean, marketing: boolean) => {
    const preferences: CookiePreferences = {
      necessary: true,
      statistics,
      marketing,
      timestamp: new Date().toISOString(),
    };

    setCookie('advieskeuze_cookie_consent', JSON.stringify(preferences), 365);
    updateConsent(statistics, marketing);
    setOpen(false);
  };

  const handleAcceptAll = () => savePreferences(true, true);
  const handleRejectAll = () => savePreferences(false, false);
  const handleSavePreferences = () =>
    savePreferences(statisticsConsent, marketingConsent);

  if (!open) return null;

  return (
    <div
      className="fixed inset-4 max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl md:bottom-6 md:left-auto md:right-6 md:top-auto md:max-w-3xl"
      style={{ zIndex: 1001 }}
    >
      <div className="mb-4 flex items-start justify-between">
        <h2 className="font-display text-2xl font-bold text-black">
          🍪 Cookie instellingen
        </h2>
        <button
          onClick={handleRejectAll}
          className="text-stone-400 transition-colors hover:text-stone-600"
          aria-label="Weiger alle cookies en sluit deze popup"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <p className="mb-6">
        Advieskeuze maakt gebruik van cookies om je browser-ervaring te
        verbeteren en ons verkeer te analyseren. Hieronder kun je kiezen met
        welke cookies je akkoord gaat.
      </p>

      <div className="mb-3 space-y-4">
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <div className="flex items-start justify-between bg-stone-50 p-4">
            <div className="flex-1">
              <h3 className="mb-1 font-display font-bold text-black">
                Verplichte cookies{' '}
                <span className="font-light">
                  ({cookieData.necessary.length})
                </span>
              </h3>
              <p className="mb-0 text-sm text-stone-600">
                Noodzakelijk voor het functioneren van de website, zoals het
                onthouden van je sessie, inlogstatus en
                beveiligingsinstellingen. Deze cookies kunnen niet worden
                uitgeschakeld.
              </p>
            </div>
            <div className="ml-4 flex items-center">
              <div className="flex h-6 w-11 cursor-not-allowed items-center justify-end rounded-full bg-stone-500 px-1">
                <div className="h-4 w-4 rounded-full bg-stone-200"></div>
              </div>
            </div>
          </div>

          {showDetails && (
            <div className="border-t border-stone-200 bg-white p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-stone-600">
                    <th className="pb-2 font-medium">Naam</th>
                    <th className="pb-2 font-medium">Doeleinde</th>
                    <th className="pb-2 font-medium">Duratie</th>
                  </tr>
                </thead>
                <tbody>
                  {cookieData.necessary.map((cookie, index) => (
                    <tr
                      key={`cookie-desc-necessary-${index}`}
                      className="border-b last:border-b-0"
                    >
                      <td className="font-mono py-3 text-xs">{cookie.name}</td>
                      <td className="py-3 pr-2 text-stone-700 md:w-[60%]">
                        {cookie.purpose}
                      </td>
                      <td className="py-3 text-stone-600">{cookie.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {cookieData.statistics?.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-stone-200">
            <div className="flex items-start justify-between bg-stone-50 p-4">
              <div className="flex-1">
                <h3 className="mb-1 font-display font-bold text-black">
                  Statistiek cookies{' '}
                  <span className="font-light">
                    ({cookieData.statistics.length})
                  </span>
                </h3>
                <p className="mb-0 text-sm text-stone-600">
                  Help ons inzicht krijgen in bezoekersinteracties, zoals
                  bezoekersstatistieken, heatmaps en sessie-opnames, om de
                  gebruikerservaring van onze website te verbeteren.
                </p>
              </div>
              <div className="ml-4 flex items-center">
                <button
                  onClick={() => setStatisticsConsent(!statisticsConsent)}
                  className={cn(
                    'flex h-6 w-11 items-center rounded-full px-1 transition-colors',
                    statisticsConsent
                      ? 'justify-end bg-blue-600'
                      : 'justify-start bg-stone-300'
                  )}
                  aria-label="Toggle marketing cookies"
                >
                  <div className="h-4 w-4 rounded-full bg-white"></div>
                </button>
              </div>
            </div>

            {showDetails && (
              <div className="border-t border-stone-200 bg-white p-4">
                <CookieTable cookies={cookieData.statistics} />
              </div>
            )}
          </div>
        )}

        {cookieData.marketing?.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-stone-200">
            <div className="flex items-start justify-between bg-stone-50 p-4">
              <div className="flex-1">
                <h3 className="mb-1 font-display font-bold text-black">
                  Marketing cookies{' '}
                  <span className="font-light">
                    ({cookieData.marketing.length})
                  </span>
                </h3>
                <p className="mb-0 text-sm text-stone-600">
                  Worden gebruikt door advertentiepartners zoals Google, Meta en
                  LinkedIn om advertenties te personaliseren en de effectiviteit
                  van onze campagnes te meten.
                </p>
              </div>
              <div className="ml-4 flex items-center">
                <button
                  onClick={() => setMarketingConsent(!marketingConsent)}
                  className={cn(
                    'flex h-6 w-11 items-center rounded-full px-1 transition-colors',
                    marketingConsent
                      ? 'justify-end bg-blue-600'
                      : 'justify-start bg-stone-300'
                  )}
                  aria-label="Toggle marketing cookies"
                >
                  <div className="h-4 w-4 rounded-full bg-white"></div>
                </button>
              </div>
            </div>

            {showDetails && (
              <div className="border-t border-stone-200 bg-white p-4">
                <CookieTable cookies={cookieData.marketing} />
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mb-6 flex w-full items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
      >
        {showDetails ? (
          <>
            <FontAwesomeIcon icon={faChevronUp} />
            Cookie details verbergen
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faChevronDown} />
            Cookie details tonen
          </>
        )}
      </button>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button variant="outline" className="w-full" onClick={handleRejectAll}>
          Alles weigeren
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={handleSavePreferences}
        >
          Selectie opslaan
        </Button>
        <Button variant="primary" className="w-full" onClick={handleAcceptAll}>
          Alles accepteren
        </Button>
      </div>

      <p className="mb-0 mt-4 text-center text-xs">
        Voor meer informatie lees ons{' '}
        <a href="/home/privacy" className="underline">
          Privacybeleid
        </a>{' '}
        en{' '}
        <a href="/home/cookiebeleid" className="underline">
          Cookiebeleid
        </a>
        .
      </p>
    </div>
  );
};

export default CookieConsent;
